import React, { useState } from "react";
import { api } from "@/lib/api";
import { setCustomerSession, getCustomerSession, clearCustomerSession } from "@/lib/showroomSession";
import { Sparkles, X, Loader2, UserCircle2 } from "lucide-react";

/**
 * Modal shown on the showroom tablet whenever no customer session is active.
 * Staff enters customer name + 10-digit mobile → backend creates/resumes session.
 */
export default function CustomerOnboarding({ open, onClose, onStarted }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const start = async (e) => {
    e?.preventDefault();
    if (!name.trim() || mobile.replace(/\D/g, "").length < 10) {
      alert("Please enter the customer's name and a valid 10-digit mobile number.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/showroom/session/start", { name, mobile });
      setCustomerSession(data);
      onStarted?.(data);
      onClose?.();
      setName("");
      setMobile("");
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't start session. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="customer-onboarding-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md rounded-sm shadow-2xl"
      >
        <div className="px-5 py-4 border-b border-[#E8E6DF] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[#B5953B]" />
            <div className="font-semibold text-sm">Welcome to Heda</div>
          </div>
          <button
            onClick={onClose}
            className="text-[#5C5C54] hover:text-[#1A1A18]"
            data-testid="customer-onboarding-close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        <form onSubmit={start} className="p-5 space-y-4">
          <div>
            <div className="hed-overline mb-1">Customer name</div>
            <input
              data-testid="customer-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mr. Arjun Mehta"
              autoFocus
              className="w-full bg-[#FAFAF8] border border-[#E8E6DF] focus:border-[#B5953B] rounded-sm px-3 py-2.5 text-sm outline-none"
            />
          </div>
          <div>
            <div className="hed-overline mb-1">Mobile number</div>
            <input
              data-testid="customer-mobile-input"
              inputMode="numeric"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/[^\d+\-\s]/g, ""))}
              placeholder="98xxxxxxxx"
              className="w-full bg-[#FAFAF8] border border-[#E8E6DF] focus:border-[#B5953B] rounded-sm px-3 py-2.5 text-sm outline-none"
            />
            <p className="text-[11px] text-[#5C5C54] mt-1.5">
              If this customer has visited before, their wishlist and history will load automatically.
            </p>
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="customer-onboarding-start"
            className="w-full bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white font-semibold py-3 rounded-sm transition-colors inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UserCircle2 size={14} />}
            Start showroom session
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Small staff control rendered in the admin top-bar showing active customer session,
 * with buttons to start a new session or end the current one.
 */
export function CustomerSessionPill() {
  const [session, setSession] = useState(() => getCustomerSession());
  const [open, setOpen] = useState(false);

  // Listen for session changes from anywhere in the app.
  React.useEffect(() => {
    const handler = () => setSession(getCustomerSession());
    window.addEventListener("heda-customer-session-changed", handler);
    return () => window.removeEventListener("heda-customer-session-changed", handler);
  }, []);

  const end = async () => {
    if (!window.confirm("End this customer session?")) return;
    try {
      await api.post("/showroom/session/end");
    } catch {
      /* still clear locally */
    }
    clearCustomerSession();
    setSession(null);
  };

  return (
    <>
      {session ? (
        <div
          className="inline-flex items-center gap-2 text-xs bg-[#FDF7E3] border border-[#B5953B]/40 rounded-sm px-2.5 py-1.5"
          data-testid="customer-session-pill"
        >
          <UserCircle2 size={12} strokeWidth={2} className="text-[#5E6033]" />
          <span className="font-semibold text-[#1A1A18]">{session.name}</span>
          <span className="text-[#5C5C54]">·{session.mobile.slice(-4)}</span>
          {session.is_returning && (
            <span className="text-[10px] uppercase tracking-wider text-[#5E6033]">
              Visit #{session.visit_count}
            </span>
          )}
          <button
            onClick={end}
            className="ml-1 text-[#5C5C54] hover:text-[#9E2A2B]"
            title="End session"
            data-testid="customer-session-end"
          >
            <X size={11} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid="customer-session-start-button"
          className="inline-flex items-center gap-1.5 text-xs bg-[#1A1A18] text-white px-2.5 py-1.5 rounded-sm hover:bg-[#2a2a26] transition-colors"
        >
          <UserCircle2 size={12} strokeWidth={2} />
          Start customer session
        </button>
      )}
      <CustomerOnboarding open={open} onClose={() => setOpen(false)} onStarted={setSession} />
    </>
  );
}
